import { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import tedsLogo from '@/assets/teds-logo-transparent.png';

const navLinks = [
  { name: 'Why Choose Us', href: '/#why-us' },
  { name: 'About', href: '/#about' },
  { name: 'Contact', href: '/#contact' },
];

const productLinks = [
  { name: 'Roman Shades', href: '/roman-shades' },
  { name: 'Plantation Wood Shutters', href: '/plantation-wood-shutters' },
  { name: 'Cellular Shades', href: '/cellular-shades' },
  { name: 'Roller Shades', href: '/roller-shades' },
  { name: 'Sun Screens', href: '/sun-screens' },
  { name: 'Motorized Shades', href: '/motorized-shades' },
  { name: 'Smart Drapes', href: '/smart-drapes' },
  { name: 'Zebra Shades', href: '/zebra-shades' },
  { name: 'Faux Wood Blinds', href: '/faux-wood-blinds' },
  { name: 'Wood Blinds', href: '/wood-blinds' },
  { name: 'Patio Shades', href: '/patio-shades' },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-soft py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={tedsLogo} 
              alt="Ted's Shades & Shutters" 
              className="h-12 md:h-14 w-auto"
              width={100}
              height={56}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <a
                href="/#products"
                className="link-underline text-sm font-medium text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                Products
                <ChevronDown className={`w-4 h-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
              </a>
              
              {isProductsOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="bg-card rounded-lg shadow-medium border border-border py-2 min-w-[200px]">
                    {productLinks.map((product) => (
                      <Link
                        key={product.name}
                        to={product.href}
                        className="block px-4 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-foreground transition-colors"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="link-underline text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* CTA & Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:6026914066"
              className="flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-accent transition-colors"
            >
              <Phone className="w-4 h-4" />
              602.691.4066
            </a>
            <Button variant="default" size="sm" asChild>
              <a href="/#contact">Free Same Day Estimates</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden absolute top-full left-0 right-0 bg-card shadow-medium animate-fade-in">
            <div className="container-custom py-6 flex flex-col gap-4">
              {/* Mobile Products Dropdown */}
              <div>
                <button
                  onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                  className="w-full flex items-center justify-between text-base font-medium text-foreground/80 hover:text-accent transition-colors py-2"
                >
                  Products
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileProductsOpen && (
                  <div className="pl-4 mt-2 space-y-2 border-l-2 border-sage/30">
                    {productLinks.map((product) => (
                      <Link
                        key={product.name}
                        to={product.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-sm text-foreground/70 hover:text-accent transition-colors py-1"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-base font-medium text-foreground/80 hover:text-accent transition-colors py-2"
                >
                  {link.name}
                </a>
              ))}
              <hr className="border-border my-2" />
              <a
                href="tel:6026914066"
                className="flex items-center gap-2 text-base font-medium text-foreground py-2"
              >
                <Phone className="w-5 h-5" />
                602.691.4066
              </a>
              <Button variant="default" className="w-full mt-2" asChild>
                <a href="/#contact">Free Same Day Estimates</a>
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
